import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

/** The order the toggle cycles through. */
export const NEXT_THEME: Record<Theme, Theme> = {
  system: 'light',
  light: 'dark',
  dark: 'system',
};

/**
 * Light/dark preference, remembered per origin.
 *
 * `localStorage` is scoped to the origin, so a choice made on a local dev
 * server does not follow you to the deployed site - they are different origins
 * and the browser keeps their storage apart. `system` follows the OS setting,
 * which is why a fresh visit can look light even to someone who works in dark.
 *
 * Lives here rather than in the app shell because the landing and login pages
 * need it too, and those render before anyone is signed in.
 */
export function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return (localStorage.getItem('dca.theme') as Theme) ?? 'system';
    } catch {
      return 'system';
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('dca.theme', theme);
    } catch {
      /* storage unavailable - the choice just does not persist */
    }
  }, [theme]);

  return [theme, setTheme];
}
